import React from "react";

import "./sidebar.scss";

import {Accordion, AccordionTitleProps, Button, Divider, Menu, Sidebar as SemanticSidebar} from "semantic-ui-react";

import {SidebarConfiguration, SidebarControlsConfiguration} from "../../../config/sidebar-configuration";
import {Legend} from "./legend/legend";
import {Legend as CoreLegend} from "../../../core/components/legend/legend";
import {Printing} from "../../../core/components/printing/printing";
import {Sharing} from "../../../core/components/sharing/sharing";
import {Icon} from "../../../core/components/icon/icon";

import {observer} from "mobx-react";
import {MapFinder} from "../../../core/components/map-finder/map-finder";
import {autorun, runInAction} from "mobx";
import {Tools} from "../../../core/components/tools/tools";
import {TopicSwitcher} from "../../../core/components/topic-switcher/topic-switcher";
import {TopicSwitcherButton} from "../../../core/components/topic-switcher/topic-switcher-button";

import {IAppStore, withStore} from "../../../core/stores/abstract-app-store";
import {LayerSelector} from "../../../core/components/layer-selector/layer-selector";
import {SteckbriefSelector} from "../steckbrief-selector/steckbrief-selector";

interface CollapseButtonProps {
    onClick: any;
    visible: boolean;
}

const CollapseButton = (props: CollapseButtonProps) => (
    <Button icon size={"mini"} attached="bottom"
        className={`geo-sidebar-collapse-button ${props.visible ? "open" : "closed"}`}
        onClick={props.onClick}>
        <Icon name={props.visible ? "navigate_open" : "navigate_close"} />
        {props.visible ? "schliessen" : "Menü"}
    </Button>
);

export enum DockPosition {
    TopLeft = "docked-top-left",
    TopRight = "docked-top-right",
    BottomLeft = "docked-bottom-left",
    BottomRight = "docked-bottom-right",
    None = ""
}

export enum WindowMode {
    Normal = "normal",
    Fullscreen = "fullscreen",
    Docked = "docked"
}

interface SidebarProps {
    config: SidebarConfiguration;
    small?: boolean;
    store?: IAppStore; // this is injected by withStore
}

@observer
class SidebarBase extends React.Component<SidebarProps, any>{
    breakToDocked = 510;  // break point where to switch to docked mode
    autorunDisposers: any[] = [];
    lastActiveIndex: string[] = [];
    resizeObserver: ResizeObserver;

    widgetKey = "sidebar";
    sidebarRef = React.createRef<HTMLDivElement>();
    constructor(props: SidebarProps) {
        super(props);
        // noinspection PointlessBooleanExpressionJS,RedundantConditionalExpressionJS
        this.state = {
            activeIndex: props.config.activeIndex || ["toc"],
            width: this.props.small? "100%" : props.config.width
        };
        this.lastActiveIndex = props.config.activeIndex || ["toc"];
        this.checkHeight = this.checkHeight.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.hide = this.hide.bind(this);
        this.toggleVisibility = this.toggleVisibility.bind(this);
    }
    handleResize(): void {
        this.checkHeight();
    }
    checkHeight(): void {
        const {store} = this.props;
        const sHeight = this.sidebarRef.current?.clientHeight;
        const isHigh = (store?.uiStore.uiHeight && sHeight && (store?.uiStore.uiHeight - 95 < sHeight)) as boolean;
        if (store?.uiStore.sidebarHigh !== isHigh) {
            store?.uiStore.setSidebarHigh(isHigh);
        }
    }
    componentDidMount(): void {
        const {config, small, store} = this.props;
        if (!store) {
            return;
        }

        runInAction(() => {
            if (!store.uiStore.widgets.has(this.widgetKey)) {
                store.uiStore.addWidget(this.widgetKey, this);
                store.uiStore.setMenuVisible(!config.collapsed);
            }
        });
        if (small){
            this.autorunDisposers.push(autorun(() => {
                const {visible} = store.TopicSwitcherStore;
                this.toggleTopicMenu(visible);
            }));
        } else {
            this.checkHeight();
            this.resizeObserver = new ResizeObserver(() => {
                this.checkHeight();
            });
            if (this.sidebarRef.current){
                this.resizeObserver.observe(this.sidebarRef.current);
            }
            window.addEventListener("resize", this.handleResize);
        }
    }
    componentDidUpdate(prevProps: SidebarProps): void {
        const {store} = this.props;
        if(prevProps.config.activeIndex !== this.props.config.activeIndex){
            this.setState({activeIndex: this.props.config.activeIndex || ["toc"]});
            this.lastActiveIndex = this.props.config.activeIndex || ["toc"];
        }
        if(prevProps.config.collapsed !== this.props.config.collapsed){
            store?.uiStore.setMenuVisible(!this.props.config.collapsed);
        }
    }
    componentWillUnmount(): void {
        const {small, store} = this.props;
        store?.uiStore.removeWidget(this.widgetKey);
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        if(!small){
            window.removeEventListener("resize", this.handleResize);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    /**
     * Handle clicks to accordion titles
     *
     * @param e
     * @param titleProps
     */
    handleClick(e: React.MouseEvent<HTMLDivElement>, titleProps: AccordionTitleProps): void {
        const {store} = this.props;
        runInAction(()=> {
            const { activeIndex } = this.state;
            let newIndex: string[] = [];
            const index = titleProps.index as "basemap_gallery" | "legend" | "map_finder" | "print"
            | "sharing" | "toc" | "topic_menu" | "tools" | "tools_sharing";
            const isSmallDevice = window.matchMedia(
                "(max-width: "+ store?.mapViewerConfig.breakpoints.md + "px)"
            ).matches;
            if (activeIndex.includes(index)) {
                // hide tab by removing index from list
                newIndex = activeIndex.filter((item: string) => item !== index);
            } else {
                // show panel by adding to list
                if (isSmallDevice || this.props.config.exclusivePanels?.includes(index)) {
                    // only open the exclusive tab
                    newIndex = [index as string];
                } else {
                    newIndex = [...activeIndex, index];
                }
            }
            if(!newIndex.includes("topic_menu")) {
                this.lastActiveIndex = newIndex;
            }
            this.setState( { activeIndex: newIndex });
            if(this.props.small) {
                store?.TopicSwitcherStore.setVisible(newIndex.includes("topic_menu"));
            }
        });
    }
    toggleTopicMenu(visible: boolean): void {
        const {store} = this.props;
        runInAction(()=> {
            store?.TopicSwitcherStore.setVisible(visible);
            if(visible){
                this.setState({
                    activeIndex: ["topic_menu"]
                });
            }else{
                if(this.lastActiveIndex.length > 0) {
                    this.setState({activeIndex: this.lastActiveIndex});
                }
            }
        });
    }

    hide(): void {
        const {store} = this.props;
        store?.uiStore.setMenuVisible(false);
    }

    dock(): void {
        this.setState((state: any) => {
            if (state.currentMode !== WindowMode.Docked) {
                return {
                    currentMode: WindowMode.Docked,
                    dockPosition: DockPosition.TopRight,
                    width: "100vw"
                };
            }
        });
    }

    undock(): void {
        this.setState((state: any) => {
            if (state.currentMode !== WindowMode.Normal) {
                // go back to original size and position
                return {
                    currentMode: WindowMode.Normal,
                    dockPosition: DockPosition.None,
                    width: this.props.config.width
                };
            }
        });
    }

    toggleVisibility(): void {
        const {store} = this.props;
        const currentVisible = store?.uiStore.menuVisible;
        store?.uiStore.setMenuVisible(!currentVisible);
    }

    render(): JSX.Element | null {
        const {activeIndex, width, currentMode} = this.state;
        const {config, small, store} = this.props;
        if (!store) {
            return null;
        }

        const {menuVisible} = store.uiStore;
        const topicswitcherVisible = store.TopicSwitcherStore.visible;
        const controlsConfig = store.mapViewerConfig as SidebarControlsConfiguration;
        const panels = [];
        if(store.CompareStore.enabled && store.mapViewerConfig.compare.style === "slider" && !small){
            return null;
        }
        const content = (
            <LayerSelector
                config={store.mapViewerConfig.layerSelector}
                controlsConfig={controlsConfig}
                small={!!small} />
        );
        const activeCompare = (
            <span>
                <Icon name={"documents_empty"} className="right" />&nbsp;
            </span>
        );
        panels.push({
            key: "layer-selector",
            active: activeIndex.includes("layer-selector"),
            title: {
                content: (
                    <span>
                        <Icon name="navigate_plus" />
                        <Icon name="navigate_minus" />
                        <Icon name="map_roads" /> <span>Karteninhalt</span>
                        {store.LayerSelectorStore.comparisonEnabled &&
                            activeCompare
                        }
                    </span>),
                icon: false,
                index: "layer-selector",
                onClick: this.handleClick
            },
            content: {
                content,
            }
        });
        if (!config.disabledPanels.includes("legend")) {
            let topicSwitcher: JSX.Element | null = null;
            if(store.mapViewerConfig.topicSwitcher?.visible?.includes("legend")){
                topicSwitcher = <TopicSwitcherButton topics={store.mapViewerConfig.subtopics} />;
            }
            panels.push({
                key: "legend",
                active: activeIndex.includes("legend"),
                title: {
                    content: (<span>
                        <Icon name="navigate_plus" />
                        <Icon name="navigate_minus" />
                        <Icon name="list_style_bullets" /> <span>Legende</span>
                    </span>),
                    icon: false,
                    index: "legend",
                    onClick: this.handleClick
                },
                content: {
                    content: (controlsConfig.legend.useEsri && controlsConfig.legend.esriConfig)?(
                        <CoreLegend
                            controlsConfig={controlsConfig}
                            config={controlsConfig.legend.esriConfig}
                            small={!!small}
                            topicSwitcher={topicSwitcher}
                        />
                    ) :(
                        <Legend config={controlsConfig.legend} />
                    ),
                }
            });
        }

        panels.push({
            key: "steckbrief-selector",
            active: activeIndex.includes("steckbrief-selector"),
            title: {
                content: (
                    <span>
                        <Icon name="navigate_plus"/>
                        <Icon name="navigate_minus"/>
                        <Icon name="document_pinned"/> <span>Steckbriefe</span>
                    </span>),
                icon: false,
                index: "steckbrief-selector",
                onClick: this.handleClick
            },
            content: {
                content: (
                    <SteckbriefSelector
                        controlsConfig={controlsConfig}
                        small={!!small}/>
                )
            }
        })

        if (!config.disabledPanels.includes("print")) {
            panels.push({
                key: "print",
                active: activeIndex.includes("print"),
                title: {
                    content: (
                        <span>
                            <Icon name="navigate_plus" />
                            <Icon name="navigate_minus" />
                            <Icon name="printer" /> <span>Drucken</span>
                        </span>),
                    icon: false,
                    index: "print",
                    onClick: this.handleClick
                },
                content: {
                    content: (
                        <Printing
                            config={controlsConfig.print}
                            small={!!small}
                            visible={activeIndex.includes("print")}/>
                    )
                }
            });
        }
        if (!config.disabledPanels.includes("sharing") || !config.disabledPanels.includes("tools")) {
            const panelContent: JSX.Element[] = [];
            if (!config.disabledPanels.includes("sharing")){
                panelContent.push(<Sharing
                    key={"sharing"}
                    config={controlsConfig.print}
                    small={!!small}
                    visible={activeIndex.includes("tools_sharing")
                        || activeIndex.includes("sharing") || activeIndex.includes("tools")}/>);
            }
            if (!config.disabledPanels.includes("sharing") && !config.disabledPanels.includes("tools")) {
                panelContent.push(<Divider key={"tool_divider"} className={"geo-tool-divider"}></Divider>);
            }
            if (!config.disabledPanels.includes("tools")) {
                panelContent.push(<div key={"tools"}><Tools
                    config={store.mapViewerConfig.tools}
                    small={!!small}/></div>);
            }
            panels.push({
                key: "tools_sharing",
                active: activeIndex.includes("sharing") || activeIndex.includes("tools")
                    || activeIndex.includes("tools_sharing"),
                title: {
                    content: (
                        <span>
                            <Icon name="navigate_plus" />
                            <Icon name="navigate_minus" />
                            <Icon name="share" /> <span>Teilen & Tools</span>
                        </span>),
                    icon: false,
                    index: "tools_sharing",
                    onClick: this.handleClick
                },
                content: {
                    content: panelContent
                }
            });
        }

        // in sidebar mode
        if (small){
            if (store.mapViewerConfig.meta?.mapFinder) {
                panels.push({
                    key: "map_finder",
                    active: activeIndex.includes("map_finder"),
                    title: {
                        content: (<span>
                            <Icon name="navigate_plus"/>
                            <Icon name="navigate_minus"/>
                            <Icon name="map"/> <span>weitere Karten</span>
                        </span>),
                        icon: false,
                        index: "map_finder",
                        onClick: this.handleClick
                    },
                    content: {
                        content: (
                            <MapFinder small={true} />
                        )
                    }
                });
            }
            if (!config.disabledPanels.includes("topic_menu")
                && store.mapViewerConfig.topicSwitcher && store.mapViewerConfig.subtopics) {
                panels.unshift({
                    key: "topic_menu",
                    active: activeIndex.includes("topic_menu"),
                    title: {
                        content: (
                            <span>
                                <Icon name="navigate_plus"/>
                                <Icon name="navigate_minus"/>
                                <Icon name="signpost"/> <span>{store.mapViewerConfig.topicSwitcher.title}</span>
                            </span>
                        ),
                        icon: false,
                        index: "topic_menu",
                        onClick: this.handleClick
                    },
                    content: {
                        content: (
                            <TopicSwitcher topics={store.mapViewerConfig.subtopics} small={true} />
                        )
                    }
                });
            }
        }

        const cssProperties: any = { width };
        cssProperties["--panels"] = panels.length;
        if(small) {
            return <div
                className={`geo-sidebar geo-component geo-widget ${currentMode} geo-panels-${panels.length}`}
                style={cssProperties}
                ref={this.sidebarRef}>
                <Accordion
                    className={`geo-sidebar-accordion ${menuVisible || small ? "open" : "closed"}`}
                    styled fluid={currentMode === WindowMode.Docked}
                    exclusive={false}
                    panels={panels} />
            </div>;
        }
        return <div
            className={`geo-sidebar geo-component geo-widget ${currentMode} geo-panels-${panels.length}`}
            style={cssProperties}>
            <SemanticSidebar
                as={Menu}
                animation="overlay"
                direction={"top"}
                vertical
                visible={(menuVisible && !topicswitcherVisible)}>
                <div ref={this.sidebarRef}><Accordion
                    className=
                        {`geo-sidebar-accordion ${(menuVisible && !topicswitcherVisible) || small ? "open" : "closed"}`}
                    styled fluid={currentMode === WindowMode.Docked}
                    exclusive={false}
                    panels={panels} />
                <CollapseButton onClick={this.toggleVisibility} visible={(menuVisible && !topicswitcherVisible)} />
                </div>
            </SemanticSidebar>
            <CollapseButton onClick={this.toggleVisibility} visible={(menuVisible && !topicswitcherVisible)} />
        </div>;

    }
}

export const Sidebar = withStore(SidebarBase);
